
	import { ValidationObserver, ValidationProvider } from 'vee-validate'
	import { VueTelInput } from 'vue-tel-input'
	import LocationSearchBox from '~/components/location-search-box'
	import GoogleMap from '~/components/google-map'

	const initialState = () => ({
		searchInput: '',
		customers: [],
		customer: {
			last_name: '',
			email: '',
			number: '',
			code: '',
			country: {
				code: '',
				name: ''
			}
		},
		selectedCustomer: false,
		orderType: null,
		selectedDeliveryAddress: null,
		editedDeliveryAddress: null,
		deliveryAddress: {
			annotation: 'Home',
			line: '',
			area: '',
			city: '',
			state: '',
			country: '',
			zip: '',
			landmark: ''
		},
		deliveryAddresses: [],
		markers: [],
		mapConfig: {
			zoom: 16,
			zoomControl: true,
			centerMarker: true,
			disableDefaultUI: true
		},
		showMapOverlay: false,
		cursorPos: 0
	})

	export default {
		components: {
			LocationSearchBox,
			GoogleMap,
			ValidationObserver,
			ValidationProvider,
			VueTelInput
		},
		data () {
			return {
				hasCredit: true,
				hasCOD: true,
				isFocused: false,
				blurFromPad: false,
				...initialState()
			}
		},
		computed: {
			locale () {
				return this.$store.state.locale
			},
			deviceId () {
				return this.$store.state.deviceId
			},
			merchant () {
				return this.$store.state.selectedMerchant || this.$store.state.merchant
			},
			location () {
				return this.$store.state.location
			},
			settings () {
				return this.$store.state.settings
			},
			cart: {
				get () {
					return this.$store.state.cart
				},
				set (value) {
					this.$store.commit('setState', { key: 'cart', value })
				}
			},
			isOnline () {
				return this.$store.state.isOnline
			},
			currentDeliveryAddressHasCoordinates () {
				return this.deliveryAddresses[this.selectedDeliveryAddress]?.coordinates?.filter(Boolean).length
			},
			isCustomerSearchMode () {
				return !this.settings.general?.caller_order_opening_screen || this.settings.general?.caller_order_opening_screen === 'search_customer'
			},
			isNumberPadMode () {
				return this.settings.general?.caller_order_opening_screen === 'number_pad'
			}
		},
		watch: {
			isOnline () {
				this.calculateDeliveryDistance()
			},
			selectedDeliveryAddress () {
				this.calculateDeliveryDistance()
			}
		},
		async beforeMount () {
			const paymentMethods = await this.$bridge.getPaymentMethods(this.deviceId, '')

			this.paymentMethods = typeof paymentMethods === 'string' ? JSON.parse(paymentMethods) : paymentMethods
			this.hasCredit = this.paymentMethods.findIndex(pm => pm.slug === 'credit') !== -1
			this.hasCOD = this.paymentMethods.findIndex(pm => pm.slug === 'cod') !== -1
			this.markers = [{ position: { lat: Number(this.location.latitude), lng: Number(this.location.longitude) } }]
		},
		methods: {
			onCountryChanged (country) {
				this.customer = {
					...this.customer,
					country: {
						code: country.iso2 || this.customer.country?.code,
						name: country.name || this.customer.country?.name
					},
					callingCode: `${country.dialCode || this.customer?.callingCode || this.merchant.businessPhone.calling_code}`
				}

				if (this.customer?.phone) {
					this.onPhoneInput(this.customer.phone)
				}
			},
			init () {
				if (this.cart.storeOrder) {
					this.customer = this.cart.storeOrder.customer
					this.selectedCustomer = true
					this.orderType = this.cart.storeOrder.orderType
					this.deliveryAddresses = this.customer.custom_attributes.delivery_address
					this.deliveryAddress = this.cart.storeOrder.deliveryAddress
					this.selectedDeliveryAddress = this.cart.storeOrder.deliveryAddress?.id

					if (this.currentDeliveryAddressHasCoordinates) {
						const coordinates = this.deliveryAddresses[this.selectedDeliveryAddress].coordinates

						this.markers = [{ position: { lat: coordinates[1], lng: coordinates[0] } }]
					}
				}
			},
			confirmBeforeReset () {
				this.$swal({
					title: this.$t('reset'),
					text: this.$t('resetAllFields'),
					icon: 'warning',
					buttons: [this.$tc('cancel', 1), this.$t('proceed')]
				}).then((res) => {
					if (res) {
						this.resetAll()
						this.cart.storeOrder = null
					}
				})
			},
			resetAll () {
				if (!this.cart.storeOrder) {
					this.$root.$emit('select-default-price-category')
				}

				Object.assign(this.$data, initialState())
				this.markers = [{ position: { lat: Number(this.location.latitude), lng: Number(this.location.longitude) } }]
				this.$refs.customerValidator?.reset()
				this.$refs.orderTypeValidator?.reset()
				this.$refs.deliveryAddressValidator?.reset()
			},
			handleFocus () {
				this.isFocused = true
			},
			handleBlur () {
				if (this.blurFromPad) {
					this.blurFromPad = false

					return
				}

				this.isFocused = false
				this.cursorPos = this.searchInput.length
			},
			onPadMouseDown () {
				if (this.isFocused) {
					this.blurFromPad = true
				}
			},
			async getCustomers (searchTerm) {
				if (searchTerm) {
					let customers = await this.$bridge.getCustomers(
						this.deviceId,
						this.objToJson(this.isNumberPadMode
							? { id: +searchTerm }
							: { search_term: searchTerm })
					)

					customers = (
						typeof customers === 'string' ? JSON.parse(customers) : customers
					)?.data || []

					if (this.isCustomerSearchMode) {
						this.customers = customers
					} else if (this.isNumberPadMode) {
						customers.length ? this.selectCustomer(customers[0]) : this.customer = { ...this.customer, phone: searchTerm, last_name: '', email: '' }
					}
				} else {
					this.customers = []
				}
			},
			selectCustomer (customer) {
				if (this.$refs.searchTerm) {
					this.$refs.searchTerm.$el.value = ''
				}

				const existingCustomer = this.customer

				this.customers = []
				this.customer = { ...customer, custom_attributes: { ...customer.custom_attributes, ...this.customer.custom_attributes } }

				if (this.isNumberPadMode && this.settings.general?.allow_multi_country_customers) {
					this.customer = { ...this.customer, ...existingCustomer }
				}

				if (!customer.custom_attributes?.delivery_address || // customers with no delivery address
					(Array.isArray(customer.custom_attributes?.delivery_address) &&
						!customer.custom_attributes?.delivery_address.length)) { // customers with empty delivery address array
					this.customer.custom_attributes.delivery_address = [customer.address]
				} else if (!Array.isArray(customer.custom_attributes?.delivery_address)) { // customers with old delivery address (not an array)
					this.customer.custom_attributes.delivery_address = [this.customer.custom_attributes.delivery_address]
				}

				this.customer.custom_attributes = {
					...this.customer.custom_attributes,
					callingCode: this.customer?.callingCode || this.merchant.businessPhone.calling_code,
					countryCode: this.customer?.country?.code || this.customer?.custom_attributes?.countryCode || this.merchant.countryCode
				}
				this.deliveryAddresses = this.customer.custom_attributes.delivery_address.map((da, i) => {
					return {
						line: '',
						area: '',
						city: '',
						state: '',
						country: '',
						zip: '',
						landmark: '',
						...da,
						id: i,
						annotation: da.annotation || 'Home'
					}
				})
				this.selectedDeliveryAddress = 0
				this.deliveryAddress = this.deliveryAddresses[this.selectedDeliveryAddress]

				if (this.currentDeliveryAddressHasCoordinates) {
					this.markers = [
						{
							position: {
								lat: parseFloat(this.deliveryAddresses[this.selectedDeliveryAddress].coordinates[1]),
								lng: parseFloat(this.deliveryAddresses[this.selectedDeliveryAddress].coordinates[0])
							}
						}
					]
				} else {
					this.selectedDeliveryAddress = null
				}

				this.selectedCustomer = true
			},
			async createEditCustomer () {
				if (await this.$refs.customerValidator.validate()) {
					this.selectedCustomer = !this.selectedCustomer
				}
			},
			editCurrentDeliveryAddress () {
				if (this.isOnline) {
					this.deliveryAddress = this.deliveryAddresses[this.selectedDeliveryAddress]
					this.editedDeliveryAddress = this.selectedDeliveryAddress
					this.selectedDeliveryAddress = null
				} else {
					this.$swal({
						title: this.$t('offlineError.title'),
						text: this.$t('deliveryAddressInternet'),
						icon: 'error',
						button: this.$t('ok')
					})
				}
			},
			addDeliveryAddress () {
				if (this.isOnline) {
					this.editedDeliveryAddress = this.deliveryAddresses.length
					this.deliveryAddresses[this.deliveryAddresses.length] = { id: this.deliveryAddresses.length }
					this.deliveryAddress = {
						annotation: 'Home',
						line: '',
						area: '',
						city: '',
						state: '',
						country: '',
						zip: '',
						landmark: ''
					}
					this.selectedDeliveryAddress = null
				} else {
					this.$swal({
						title: this.$t('offlineError.title'),
						text: this.$t('deliveryAddressInternet'),
						icon: 'error',
						button: this.$t('ok')
					})
				}
			},
			deleteCurrentDeliveryAddress () {
				this.deliveryAddresses.splice(this.selectedDeliveryAddress, 1)

				if (this.deliveryAddresses.length >= 1) {
					this.selectedDeliveryAddress = this.selectedDeliveryAddress > 1 ? this.selectedDeliveryAddress-- : 0
				} else if (this.isOnline) {
					this.selectedDeliveryAddress = null
					this.editedDeliveryAddress = 0
				} else {
					this.$swal({
						title: this.$t('offlineError.title'),
						text: this.$t('deliveryAddressInternet'),
						icon: 'error',
						button: this.$t('ok')
					})
				}
			},
			removeCustomer () {
				this.customer = {}
				this.selectedCustomer = false
				this.$refs.customerValidator.reset()
			},
			async saveDeliveryAddress () {
				if (await this.$refs.deliveryAddressValidator.validate()) {
					this.deliveryAddresses[this.editedDeliveryAddress || 0] = { ...this.deliveryAddress, id: (this.editedDeliveryAddress || 0) }
					this.selectedDeliveryAddress = this.editedDeliveryAddress || 0
					this.editedDeliveryAddress = null
					this.customer = {
						...this.customer,
						custom_attributes: {
							countryCode: this.customer?.country?.code || this.customer?.custom_attributes?.countryCode || this.merchant.countryCode,
							callingCode: this.customer?.callingCode || this.customer?.custom_attributes?.callingCode || this.merchant.businessPhone.calling_code,
							delivery_address: this.deliveryAddresses
						}
					}
				}
			},
			async proceed () {
				if (this.isNumberPadMode) {
					if (!this.selectedCustomer) {
						if (this.searchInput && await this.$refs.searchNumberValidator.validate()) {
							this.getCustomers(this.searchInput)
							this.searchInput = ''

							return
						} else if (this.customer.phone) {
							return this.createEditCustomer()
						}
					}

					if (this.orderType === 'delivery' && (this.selectedDeliveryAddress === null)) {
						return this.saveDeliveryAddress()
					}
				}

				if (this.isCustomerSearchMode && this.settings.general?.allow_multi_country_customers) {
					if (this.selectedCustomer && this.searchInput) {
						const isValid = await this.$refs.customerValidator?.validate?.()

						if (!isValid) { return }
					}
				}

				this.proceedToCart()
			},
			proceedToCart () {
				if (((this.orderType === 'delivery' &&
					this.hasCOD &&
					this.currentDeliveryAddressHasCoordinates) ||
					(this.orderType === 'pickup' && this.hasCredit))) {
					if (!this.customer.id) {
						this.customer = {
							...this.customer,
							id: +this.customer.phone,
							address: {
								annotation: '',
								line: '',
								area: '',
								city: '',
								state: '',
								country: '',
								zip: '',
								landmark: ''
							},
							credit_limit: 0.0,
							credit: 0.0,
							debit: 0.0,
							is_active: true,
							custom_attributes: {
								...this.customer.custom_attributes,
								delivery_address: this.deliveryAddresses
							}
						}
					}

					this.customer.custom_attributes.delivery_address = this.deliveryAddresses

					this.cart = {
						...this.cart,
						customer: this.customer,
						storeOrder: {
							customer: this.customer,
							orderType: this.orderType,
							deliveryAddress: this.deliveryAddresses[this.selectedDeliveryAddress]
						}
					}

					if (this.cart.items?.length) {
						this.$store.dispatch('cartCalculation')
					}

					this.$bvModal.hide('caller-id-modal')
				}
			},
			updateLocation (response) {
				const location = response

				this.markers = [
					{
						position: {
							lat: parseFloat(location.coordinates[1]),
							lng: parseFloat(location.coordinates[0])
						}
					}
				]

				this.deliveryAddress.area = location.area
				this.deliveryAddress.city = location.city
				this.deliveryAddress.country = location.country
				this.deliveryAddress.line = location.line
				this.deliveryAddress.state = location.state
				this.deliveryAddress.zip = location.zip
				this.deliveryAddress.coordinates = location.coordinates
				this.deliveryAddress.distance = location.distance
				this.deliveryAddress.duration = location.duration
			},
			async calculateDeliveryDistance () {
				if (this.isOnline && this.currentDeliveryAddressHasCoordinates) {
					const lat = this.deliveryAddresses[this.selectedDeliveryAddress].coordinates[1]
					const lng = this.deliveryAddresses[this.selectedDeliveryAddress].coordinates[0]
					const distance = await this.$store.dispatch('getDistance', {
						origins: `${this.location.latitude},${this.location.longitude}`,
						destinations: `${lat},${lng}`
					})

					this.deliveryAddress.distance = distance.rows[0].elements[0].distance
					this.deliveryAddress.duration = distance.rows[0].elements[0].duration
				}
			},
			onInputKeyDown (event) {
				const allowedKeys = [
					'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'
				]

				if (
					!allowedKeys.includes(event.key) &&
					!/^\d$/.test(event.key)
				) {
					event.preventDefault()
				}
			},
			onInputClick (event) {
				const inputEl = this.$refs.searchTerm?.$refs?.input || this.$refs.searchTerm?.$el.querySelector('input')

				if (inputEl) {
					this.cursorPos = event.target.selectionStart || 0
				}
			},
			onPhoneInput (value, data) {
				if (!value) {
					this.searchInput = ''
					this.cursorPos = 0

					return
				}

				this.customer = {
					...this.customer,
					custom_attributes: {
						...this.customer.custom_attributes,
						countryCode: data?.country?.iso2 || this.customer?.country?.code || this.merchant.countryCode,
						callingCode: `${data?.countryCallingCode || this.customer?.callingCode || this.merchant.businessPhone.calling_code}`
					},
					number: data?.nationalNumber || this.customer?.number,
					callingCode: `${data?.countryCallingCode || this.customer?.callingCode || this.merchant.businessPhone.calling_code}`,
					country: {
						code: data?.country?.iso2 || this.customer.country?.code,
						name: data?.country?.name || this.customer.country?.name
					},
					isValid: data?.valid || this.customer.isValid
				}
				this.searchInput = value

				if (this.isNumberPadMode) {
					const inputEl = this.$refs.searchTerm?.$refs?.input || this.$refs.searchTerm?.$el.querySelector('input')

					let start = 0
					let end = 0

					if (inputEl) {
						start = inputEl.selectionStart
						end = inputEl.selectionEnd
					}

					this.$nextTick(() => {
						if (inputEl) {
							inputEl.focus()
							inputEl.setSelectionRange(start, end)
						}
					})
				}
			},
			onNumberPadPress (d) {
				const inputEl = this.$refs.searchTerm?.$refs?.input || this.$refs.searchTerm?.$el.querySelector('input')

				this.$nextTick(() => {
					const useCursor = this.isFocused || this.blurFromPad

					if (!useCursor) { this.cursorPos = this.searchInput.length }

					if (d === 'CLEAR') {
						this.searchInput = ''
						this.cursorPos = 0
					} else if (d === 'C') {
						if (this.cursorPos > 0) {
							this.searchInput =
								this.searchInput.slice(0, this.cursorPos - 1) +
								this.searchInput.slice(this.cursorPos)
							this.cursorPos--
						}
					} else if (/^\d$/.test(d)) {
						this.searchInput =
							this.searchInput.slice(0, this.cursorPos) +
							d +
							this.searchInput.slice(this.cursorPos)
						this.cursorPos++
					} else {
						return
					}

					this.$nextTick(() => {
						if (inputEl) {
							inputEl.focus()
							inputEl.setSelectionRange(this.cursorPos, this.cursorPos)
						}
					})

					this.blurFromPad = false
				})
			}

		}
	}
